import { useEffect, useRef } from 'react'
import type { Message } from '../api/types.ts'
import { copyText } from '../lib/clipboard.ts'
import { say } from '../store/toast.ts'

/**
 * Что можно сделать с сообщением.
 *
 * Открывается правой кнопкой на компьютере и долгим нажатием на телефоне.
 * Меню само отодвигается от краёв экрана: у нижних сообщений оно иначе
 * вылезает под поле ввода, а у правых — за край.
 */

/** Ходовые реакции. Больше десятка никто не ищет, а места занимают много. */
const QUICK = ['👍', '❤️', '😂', '🔥', '😮', '😢', '🙏']

type Props = {
  message: Message
  at: { x: number; y: number }
  onClose: () => void
  onReply: () => void
  onEdit: () => void
  onDelete: () => void
  onReaction: (emoji: string) => void
  /** Есть только в группах: вдвоём хватает галочек */
  onReadBy?: () => void
  onPin: () => void
  isPinned: boolean
  onForward: () => void
  onSelect: () => void
}

export function MessageMenu({ message, at, onClose, onReply, onEdit, onDelete, onReaction, onPin, isPinned, onForward, onSelect, onReadBy }: Props) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const box = ref.current
    if (!box) return

    // Сдвигаем так, чтобы меню целиком помещалось на экране
    const rect = box.getBoundingClientRect()
    const margin = 8
    let x = at.x
    let y = at.y

    if (x + rect.width > window.innerWidth - margin) x = window.innerWidth - rect.width - margin
    if (y + rect.height > window.innerHeight - margin) y = at.y - rect.height
    if (y < margin) y = margin
    if (x < margin) x = margin

    box.style.left = `${x}px`
    box.style.top = `${y}px`
    box.style.visibility = 'visible'
  }, [at])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="menu-overlay" onClick={onClose} onContextMenu={(e) => e.preventDefault()}>
      <div
        ref={ref}
        className="msg-menu"
        style={{ visibility: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="msg-menu-reactions">
          {QUICK.map((emoji) => {
            const mine = message.reactions.some((r) => r.emoji === emoji && r.mine)
            return (
              <button
                key={emoji}
                className={'msg-menu-emoji' + (mine ? ' is-mine' : '')}
                onClick={() => {
                  onReaction(emoji)
                  onClose()
                }}
              >
                {emoji}
              </button>
            )
          })}
        </div>

        <button
          className="msg-menu-item"
          onClick={() => {
            onReply()
            onClose()
          }}
        >
          Ответить
        </button>

        <button
          className="msg-menu-item"
          onClick={() => {
            onForward()
            onClose()
          }}
        >
          Переслать
        </button>

        <button
          className="msg-menu-item"
          onClick={() => {
            onSelect()
            onClose()
          }}
        >
          Выбрать
        </button>

        <button
          className="msg-menu-item"
          onClick={() => {
            onPin()
            onClose()
          }}
        >
          {isPinned ? 'Открепить' : 'Закрепить'}
        </button>

        {message.body && (
          <button
            className="msg-menu-item"
            onClick={() => {
              void copyText(message.body ?? '').then((done) =>
                say(done ? 'Скопировано' : 'Браузер не дал доступ к буферу', done ? 'ok' : 'bad'),
              )
              onClose()
            }}
          >
            Скопировать текст
          </button>
        )}

        {/* В переписке вдвоём хватает галочек: спрашивать «кто прочитал»
            там не у кого */}
        {message.mine && onReadBy && (
          <button
            className="msg-menu-item"
            onClick={() => {
              onReadBy()
              onClose()
            }}
          >
            Кто прочитал
          </button>
        )}

        {/* Править и удалять можно только своё — сервер это тоже проверяет */}
        {message.mine && message.body && (
          <button
            className="msg-menu-item"
            onClick={() => {
              onEdit()
              onClose()
            }}
          >
            Изменить
          </button>
        )}

        {message.mine && (
          <button
            className="msg-menu-item is-danger"
            onClick={() => {
              onDelete()
              onClose()
            }}
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  )
}
